import * as _ from 'lodash';
import {
    Component,
    Input,
    HostListener,
    ViewChild,
    ElementRef,
    ViewEncapsulation,
    OnInit,
    OnDestroy
} from '@angular/core';
import { Router } from '@angular/router';

import { ImageResolution, productImagePlaceholder } from 'helpers';
import { ImageModel } from 'models';
import { ModalService, SegmentService, Price } from 'services';
import { TagEmphasis, TagSeverity, TagSize } from 'fc-design-system';

const emptyImage = {
    thumbUrl: productImagePlaceholder,
    largeUrl: productImagePlaceholder,
    originalUrl: productImagePlaceholder
};

@Component({
    selector: 'fc-order-pdp-gallery',
    templateUrl: './gallery.component.html',
    styleUrls: ['./gallery.component.less'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class PdpGalleryComponent implements OnInit, OnDestroy {
    @Input()
    public set images(images: Partial<Image>[]) {
        this.allImages = this.mapImagesToImagesWithUrls(images);
        if (_.isEmpty(this.allImages)) {
            this.activeImage = emptyImage;
        }

        this.activeImage = !_.isEmpty(this.allImages) ? _.first(this.allImages) : emptyImage;
        this.setSliderImages(0);

        this.offsetIndex = 0;
        this.isPreliminaryImage = _.some(images, { isPreliminary: true });
    }

    @Input()
    public set price(price: Price) {
        if (price) {
            this.discount = price.discount ?? 0;
        }
    }

    @ViewChild('wheelZone') public wheelZone: ElementRef;
    @ViewChild('discountBadge') public discountBadge: ElementRef<HTMLDivElement>;

    public activeImage: ImageWithUrl;
    public displayedImages: ImageWithUrl[] = [];
    public allImages: ImageWithUrl[] = [];
    public isPreliminaryImage = false;
    public previewModalId = 'image-preview-model';
    public discount = 0;

    public readonly TagSeverity = TagSeverity;
    public readonly TagEmphasis = TagEmphasis;
    public readonly TagSize = TagSize;

    public get isActiveImageAI(): boolean {
        return this.activeImage?.isAIImage === true;
    }

    private scrolling: boolean;
    private offsetIndex = 0;
    private isMobile = window.screen.width < 1280;
    private readonly maxItems: number = this.isMobile ? 3 : 6;

    public constructor(
        private imageModel: ImageModel,
        private modalService: ModalService,
        private router: Router,
        private segmentService: SegmentService
    ) {}

    public get zoomEnabled(): boolean {
        return !_.isEmpty(this.allImages);
    }

    ngOnInit(): void {
        this.modalService.add(this.previewModalId);
    }

    ngOnDestroy(): void {
        this.modalService.remove(this.previewModalId);
    }

    @HostListener('wheel', ['$event'])
    onWheel(event) {
        if (this.wheelZone.nativeElement.contains(event.target)) {
            event.preventDefault();
            event.stopPropagation();

            if (!this.scrolling) {
                this.scrolling = true;

                if (event.wheelDeltaY > 0) {
                    this.navigateSliderUp();
                }
                if (event.wheelDeltaY < 0) {
                    this.navigateSliderDown();
                }

                // 150 is magical number to not slide too fast
                setTimeout(() => (this.scrolling = false), 150);
            }
        }
    }

    @HostListener('document:keyup.escape', ['$event'])
    onEscape(event: KeyboardEvent) {
        this.modalService.close(this.previewModalId);
    }

    public isSliderActive() {
        return _.size(this.allImages) > this.maxItems;
    }

    public isSliderUpArrowActive() {
        return this.offsetIndex > 0;
    }

    public isSliderDownArrowActive() {
        return this.offsetIndex + this.maxItems < _.size(this.allImages);
    }

    public navigateSliderUp() {
        if (this.isSliderUpArrowActive()) {
            this.setSliderImages(this.offsetIndex - 1);
        }
    }

    public navigateSliderDown() {
        if (this.isSliderDownArrowActive()) {
            this.setSliderImages(this.offsetIndex + 1);
        }
    }

    public getStyleString(imageUrl: string) {
        return `background-image: url(${imageUrl})`;
    }

    public onImageClick() {
        if (this.zoomEnabled) {
            const prefix = this.router.url.includes('/preorder/') ? '[Pre]' : '[Re]';
            this.segmentService.track(`${prefix} uses zoom-in`);
            this.modalService.open(this.previewModalId);
        }
    }

    public displayAsActiveImage(image: ImageWithUrl) {
        this.activeImage = image;
    }

    public isActiveImage(image: ImageWithUrl) {
        return image === this.activeImage;
    }

    hideDiscountBadge() {
        if (this.discount) {
            this.discountBadge.nativeElement.style.display = 'none';
        }
    }

    showDiscountBadge() {
        if (this.discount) {
            this.discountBadge.nativeElement.style.display = 'block';
        }
    }

    private getImageUrl(image: Partial<Image>, size: ImageResolution): string {
        return _.get(image, '_id') ? this.imageModel.buildOrderAreaImageUrl(image._id, size) : productImagePlaceholder;
    }

    private mapImagesToImagesWithUrls(images: Partial<Image>[]): ImageWithUrl[] {
        return _.map(images, (image) => ({
            ...image,
            thumbUrl: this.getImageUrl(image, ImageResolution.Thumb),
            largeUrl: this.getImageUrl(image, ImageResolution.Large),
            originalUrl: this.getImageUrl(image, ImageResolution.Original)
        }));
    }

    private setSliderImages(offsetIndex: number): void {
        if (_.isEmpty(this.allImages)) {
            return;
        }
        if (_.size(this.allImages) < this.maxItems) {
            this.displayedImages = this.allImages;
        } else {
            this.displayedImages = _.slice(this.allImages, offsetIndex, offsetIndex + this.maxItems);
            this.offsetIndex = offsetIndex;
        }
    }
}

interface Image {
    _id: string;
    isPreliminary: boolean;
    isAIImage?: boolean;
}

interface ImageWithUrl extends Partial<Image> {
    thumbUrl: string;
    largeUrl: string;
    originalUrl: string;
}
